import React, { useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Grid,
  LinearProgress,
  Paper,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import {
  Check,
  ChevronDown,
  Clock,
  HelpCircle,
  Image as ImageIcon,
  Sparkles,
  X,
} from 'lucide-react';
import { NameEmailAvatar } from '../../../../../../ui/components/Common/NameEmailAvatar';
import {
  CCAApplicantResult,
  CCAQuestionResult,
  CCAResultSummary,
  CCAStatus,
} from '../../../../../../utils/types/cca_types';
import { useGetStorageUrlQuery } from '../../../../../../utils/api_service/ccaApi';
import {
  EnrichedResultSummary,
  computeSubdomainScores,
  useCCAResults,
} from './hooks/useCCAResults';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const toDisplayName = (s: string) => {
  if (s.includes('_')) {
    return s.split('_').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
  }
  return s.replace(/([A-Z])/g, ' $1').replace(/^./, (c) => c.toUpperCase()).trim();
};

const formatDate = (val?: number | string | null) => {
  if (val == null) return '—';
  try {
    const d = typeof val === 'number' ? new Date(val * 1000) : new Date(val);
    return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  } catch {
    return '—';
  }
};

const formatDuration = (seconds: number | null | undefined): string => {
  if (seconds == null) return '—';
  if (seconds < 60) return `${seconds}s`;
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  if (m < 60) return s > 0 ? `${m}m ${s}s` : `${m}m`;
  const h = Math.floor(m / 60);
  const rem = m % 60;
  return rem > 0 ? `${h}h ${rem}m` : `${h}h`;
};

const scoreColor = (pct: number): string => {
  if (pct >= 70) return '#059669';
  if (pct >= 50) return '#d97706';
  return '#e11d48';
};

// ─── Score display ────────────────────────────────────────────────────────────

interface ScoreDisplay {
  text: string;
  pct: number | null;
}

const getScoreDisplay = (summary: CCAResultSummary): ScoreDisplay | null => {
  if (summary.totalPct != null) {
    return { text: `${Math.round(summary.totalPct)}%`, pct: summary.totalPct };
  }
  if (summary.totalScore != null) {
    if (summary.maxScore != null) {
      const max = summary.maxScore;
      return {
        text: `${summary.totalScore}/${max} pts`,
        pct: max > 0 ? (summary.totalScore / max) * 100 : 0,
      };
    }
    return { text: `${summary.totalScore} pts`, pct: null };
  }
  return null;
};

// ─── Status badge ─────────────────────────────────────────────────────────────

const STATUS_CONFIG: Record<
  CCAStatus,
  { color: 'default' | 'info' | 'warning' | 'success' | 'error' }
> = {
  Uninvited: { color: 'default' },
  'Invite Sent': { color: 'info' },
  'In Progress': { color: 'warning' },
  Completed: { color: 'success' },
  Expired: { color: 'error' },
  Superseded: { color: 'default' },
};

const StatusBadge: React.FC<{ status: CCAStatus }> = ({ status }) => {
  const cfg = STATUS_CONFIG[status] ?? STATUS_CONFIG['Uninvited'];
  return (
    <Chip
      label={status}
      size='small'
      color={cfg.color}
      variant='filled'
      sx={{ fontWeight: 600, fontSize: '0.75rem' }}
    />
  );
};

// ─── Subdomain bar colours ────────────────────────────────────────────────────

const SUBDOMAIN_COLOR: Record<string, string> = {
  abstract_reasoning: '#8b5cf6',
  logical_reasoning: '#3b82f6',
  quantitative_aptitude: '#10b981',
  verbal_ability: '#f59e0b',
  data_interpretation: '#f43f5e',
  'Abstract Reasoning': '#8b5cf6',
  'Logical Reasoning': '#3b82f6',
  'Quantitative Aptitude': '#10b981',
  'Verbal Ability': '#f59e0b',
  'Data Interpretation': '#f43f5e',
};

const barColor = (subdomain: string) =>
  SUBDOMAIN_COLOR[subdomain] ?? '#94a3b8';

// ─── Storage image ────────────────────────────────────────────────────────────

const CCAStorageImage: React.FC<{
  imageKey: string;
  alt?: string;
  className?: string;
}> = ({ imageKey, alt = 'Image' }) => {
  const { data, isLoading, isError } = useGetStorageUrlQuery(imageKey, {
    skip: !imageKey,
  });
  if (isLoading) {
    return (
      <Skeleton variant='rounded' height={96} sx={{ width: '100%', borderRadius: 2 }} />
    );
  }
  if (isError || !data?.url) {
    return (
      <Typography variant='caption' sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
        Image unavailable
      </Typography>
    );
  }
  return (
    <Box
      component='img'
      src={data.url}
      alt={alt}
      sx={{ maxHeight: 160, maxWidth: '100%', objectFit: 'contain', borderRadius: 1.5 }}
    />
  );
};

// ─── Detail panel ─────────────────────────────────────────────────────────────

const SubdomainBreakdown: React.FC<{
  subdomainScores: Record<string, number>;
}> = ({ subdomainScores }) => {
  const entries = Object.entries(subdomainScores);
  if (entries.length === 0) return null;
  return (
    <Box>
      <Typography
        variant='caption'
        sx={{ display: 'block', mb: 1.5, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase', color: 'text.secondary' }}
      >
        Subdomain Breakdown
      </Typography>
      <Stack spacing={1.5}>
        {entries.map(([sd, score]) => (
          <Box key={sd}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant='caption' sx={{ fontWeight: 600, color: 'text.primary' }}>
                {toDisplayName(sd)}
              </Typography>
              <Typography variant='caption' sx={{ fontWeight: 700, color: scoreColor(score) }}>
                {Math.round(score)}%
              </Typography>
            </Box>
            <LinearProgress
              variant='determinate'
              value={Math.min(score, 100)}
              sx={{
                height: 6,
                borderRadius: 3,
                bgcolor: 'action.hover',
                '& .MuiLinearProgress-bar': {
                  bgcolor: barColor(sd),
                  borderRadius: 3,
                },
              }}
            />
          </Box>
        ))}
      </Stack>
    </Box>
  );
};

const QuestionAccordion: React.FC<{
  questionResults: CCAQuestionResult[] | null | undefined;
}> = ({ questionResults }) => {
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const items = (questionResults ?? []).slice().sort((a, b) => a.orderKey - b.orderKey);
  if (items.length === 0) return null;
  return (
    <Box>
      <Typography
        variant='caption'
        sx={{ display: 'block', mb: 1.5, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase', color: 'text.secondary' }}
      >
        Questions ({items.length})
      </Typography>
      <Paper variant='outlined' sx={{ borderRadius: 2, overflow: 'hidden' }}>
        {items.map((qr, i) => {
          const isOpen = expandedId === qr.id;
          const correct = qr.isCorrect;
          const snap = qr.questionSnapshot;
          const questionText = snap?.question?.text ?? '(no question text)';
          const questionImageKey = snap?.question?.imageKey ?? null;
          const isMCQ = (snap?.questionType ?? qr.questionType) === 'multiple_choice';
          const selectedOption = isMCQ
            ? snap?.options?.find((o) => o.id === qr.selectedOptionId)
            : undefined;
          const correctOption = isMCQ
            ? snap?.options?.find((o) => o.id === snap?.correctOptionId)
            : undefined;
          const level = snap?.level;
          const aiScorePct = qr.aiScore != null ? Math.round(qr.aiScore * 100) : null;

          return (
            <Box key={qr.id} sx={{ borderBottom: i < items.length - 1 ? '1px solid' : 'none', borderColor: 'divider' }}>
              <Button
                fullWidth
                onClick={() => setExpandedId(isOpen ? null : qr.id)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  p: 1.5,
                  textAlign: 'left',
                  textTransform: 'none',
                  color: 'inherit',
                  '&:hover': { bgcolor: 'action.hover' },
                }}
              >
                <Avatar
                  sx={{
                    width: 22,
                    height: 22,
                    fontSize: '0.65rem',
                    fontWeight: 700,
                    bgcolor: correct === true ? 'success.light' : correct === false ? 'error.light' : 'warning.light',
                    color: correct === true ? 'success.dark' : correct === false ? 'error.dark' : 'warning.dark',
                  }}
                >
                  {i + 1}
                </Avatar>

                <Typography
                  variant='caption'
                  sx={{
                    flex: 1,
                    fontWeight: 500,
                    color: 'text.primary',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                  }}
                >
                  {questionText}
                  {questionImageKey && (
                    <Typography component='span' variant='caption' sx={{ ml: 1, color: 'text.secondary', display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                      <ImageIcon size={12} /> image
                    </Typography>
                  )}
                </Typography>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                  {!isMCQ && aiScorePct != null ? (
                    <Typography variant='caption' sx={{ fontWeight: 700, color: scoreColor(aiScorePct) }}>
                      {aiScorePct}%
                    </Typography>
                  ) : isMCQ && correct !== null ? (
                    correct ? (
                      <Check size={16} color='#10b981' />
                    ) : (
                      <X size={16} color='#ef4444' />
                    )
                  ) : null}
                  <ChevronDown
                    size={16}
                    style={{
                      transform: isOpen ? 'rotate(180deg)' : 'none',
                      transition: 'transform 0.2s',
                      opacity: 0.5,
                    }}
                  />
                </Box>
              </Button>

              <Collapse in={isOpen}>
                <Box sx={{ p: 2, bgcolor: 'action.hover', borderTop: '1px solid', borderColor: 'divider' }}>
                  {questionImageKey && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2, p: 1.5, bgcolor: 'background.paper', borderRadius: 2 }}>
                      <CCAStorageImage imageKey={questionImageKey} alt='Question image' />
                    </Box>
                  )}

                  <Grid container spacing={2} sx={{ mb: 2 }}>
                    {/* Candidate's Answer */}
                    <Grid item xs={12} sm={4}>
                      <Typography variant='caption' sx={{ display: 'block', mb: 0.5, fontWeight: 700, textTransform: 'uppercase', color: 'text.secondary' }}>
                        Candidate&apos;s Answer
                      </Typography>
                      <Paper
                        variant='outlined'
                        sx={{
                          p: 1.5,
                          borderRadius: 2,
                          bgcolor: correct === true ? 'success.light' : correct === false ? 'error.light' : 'background.paper',
                          borderColor: correct === true ? 'success.main' : correct === false ? 'error.main' : 'divider',
                        }}
                      >
                        {isMCQ ? (
                          selectedOption ? (
                            selectedOption.imageKey ? (
                              <CCAStorageImage imageKey={selectedOption.imageKey} alt='Option' />
                            ) : (
                              <Typography variant='caption' sx={{ fontWeight: 600 }}>
                                {selectedOption.text}
                              </Typography>
                            )
                          ) : (
                            <Typography variant='caption' sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                              No answer
                            </Typography>
                          )
                        ) : qr.shortAnswerText ? (
                          <Typography variant='caption' sx={{ whiteSpace: 'pre-wrap', fontWeight: 500 }}>
                            {qr.shortAnswerText.trim()}
                          </Typography>
                        ) : (
                          <Typography variant='caption' sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                            No answer
                          </Typography>
                        )}
                      </Paper>
                    </Grid>

                    {/* Correct Answer / AI Score */}
                    <Grid item xs={12} sm={4}>
                      <Typography variant='caption' sx={{ display: 'block', mb: 0.5, fontWeight: 700, textTransform: 'uppercase', color: 'text.secondary' }}>
                        {isMCQ ? 'Correct Answer' : 'AI Score'}
                      </Typography>
                      {isMCQ ? (
                        <Paper
                          variant='outlined'
                          sx={{ p: 1.5, borderRadius: 2, bgcolor: 'success.light', borderColor: 'success.main' }}
                        >
                          {correctOption ? (
                            correctOption.imageKey ? (
                              <CCAStorageImage imageKey={correctOption.imageKey} alt='Correct' />
                            ) : (
                              <Typography variant='caption' sx={{ fontWeight: 600, color: 'success.dark' }}>
                                {correctOption.text}
                              </Typography>
                            )
                          ) : (
                            <Typography variant='caption' sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                              Not available
                            </Typography>
                          )}
                        </Paper>
                      ) : (
                        <Paper
                          variant='outlined'
                          sx={{
                            p: 1.5,
                            borderRadius: 2,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            bgcolor: 'background.paper',
                          }}
                        >
                          {aiScorePct != null ? (
                            <>
                              <Typography variant='h6' sx={{ fontWeight: 800, color: scoreColor(aiScorePct) }}>
                                {aiScorePct}%
                              </Typography>
                              <Typography variant='caption' sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'text.secondary' }}>
                                <Sparkles size={12} /> AI evaluated
                              </Typography>
                            </>
                          ) : (
                            <Typography variant='caption' sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                              Pending evaluation
                            </Typography>
                          )}
                        </Paper>
                      )}
                    </Grid>

                    {/* Ideal Answer */}
                    <Grid item xs={12} sm={4}>
                      <Typography variant='caption' sx={{ display: 'block', mb: 0.5, fontWeight: 700, textTransform: 'uppercase', color: 'text.secondary' }}>
                        Ideal Answer
                      </Typography>
                      <Paper
                        variant='outlined'
                        sx={{ p: 1.5, borderRadius: 2, bgcolor: 'info.light', borderColor: 'info.main' }}
                      >
                        {snap?.idealAnswer ? (
                          <Typography variant='caption' sx={{ color: 'info.dark', lineHeight: 1.5 }}>
                            {snap.idealAnswer}
                          </Typography>
                        ) : (
                          <Typography variant='caption' sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                            Not specified
                          </Typography>
                        )}
                      </Paper>
                    </Grid>
                  </Grid>

                  {/* Metadata pills */}
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    <Chip label={toDisplayName(qr.subdomain)} size='small' variant='outlined' sx={{ fontSize: '0.65rem', height: 20 }} />
                    {level != null && (
                      <Chip label={`Level ${level}`} size='small' variant='outlined' sx={{ fontSize: '0.65rem', height: 20 }} />
                    )}
                    <Chip
                      label={(snap?.questionType ?? qr.questionType).replace(/_/g, ' ')}
                      size='small'
                      variant='outlined'
                      sx={{ fontSize: '0.65rem', height: 20, textTransform: 'capitalize' }}
                    />
                    <Chip label={formatDuration(qr.timeTakenSeconds)} size='small' variant='outlined' sx={{ fontSize: '0.65rem', height: 20 }} />
                  </Box>
                </Box>
              </Collapse>
            </Box>
          );
        })}
      </Paper>
    </Box>
  );
};

const ResultDetail: React.FC<{
  result: CCAApplicantResult;
  isLoading: boolean;
  isError: boolean;
}> = ({ result, isLoading, isError }) => {
  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 4, gap: 1 }}>
        <CircularProgress size={16} />
        <Typography variant='caption' sx={{ color: 'text.secondary' }}>
          Loading detail…
        </Typography>
      </Box>
    );
  }
  if (isError) {
    return (
      <Typography variant='caption' sx={{ display: 'block', py: 2, color: 'error.main', textAlign: 'center' }}>
        Failed to load result detail.
      </Typography>
    );
  }
  const assessment = result.assessment;
  const rawServerScores = assessment.subdomainScores ?? {};
  const extractedServerScores: Record<string, number> = {};
  for (const [key, val] of Object.entries(rawServerScores)) {
    if (typeof val === 'object' && val !== null && 'pct' in val) {
      extractedServerScores[key] = (val as { pct: number }).pct;
    } else if (typeof val === 'number') {
      extractedServerScores[key] = val;
    }
  }
  const subdomainScores =
    Object.keys(extractedServerScores).length > 0
      ? extractedServerScores
      : computeSubdomainScores(result.responses);

  const pct = assessment.totalPct != null ? Math.round(assessment.totalPct) : null;
  const hasRawAndMax = assessment.totalScore != null && assessment.maxScore != null;
  const hasAnyScore = pct != null || hasRawAndMax || assessment.totalScore != null;

  return (
    <Box sx={{ p: 2.5, bgcolor: 'action.hover', borderTop: '1px solid', borderColor: 'divider' }}>
      {hasAnyScore && (
        <Box sx={{ mb: 2.5, display: 'flex', alignItems: 'baseline', gap: 1 }}>
          {pct != null ? (
            <>
              <Typography variant='h5' sx={{ fontWeight: 800, color: scoreColor(pct) }}>
                {pct}%
              </Typography>
              {hasRawAndMax && (
                <Typography variant='caption' sx={{ fontWeight: 600, color: 'text.secondary' }}>
                  ({assessment.totalScore}/{assessment.maxScore} pts)
                </Typography>
              )}
            </>
          ) : hasRawAndMax ? (
            <Typography variant='h5' sx={{ fontWeight: 800, color: 'text.primary' }}>
              {assessment.totalScore}
              <Typography component='span' variant='body2' sx={{ color: 'text.secondary' }}>
                /{assessment.maxScore} pts
              </Typography>
            </Typography>
          ) : (
            <Typography variant='h5' sx={{ fontWeight: 800, color: 'text.primary' }}>
              {assessment.totalScore}
              <Typography component='span' variant='body2' sx={{ ml: 0.5, color: 'text.secondary' }}>
                pts
              </Typography>
            </Typography>
          )}
        </Box>
      )}
      <Stack spacing={3}>
        <SubdomainBreakdown subdomainScores={subdomainScores} />
        <QuestionAccordion questionResults={result.responses} />
      </Stack>
    </Box>
  );
};

// ─── Results row ──────────────────────────────────────────────────────────────

interface ResultRowProps {
  summary: EnrichedResultSummary;
  isExpanded: boolean;
  onToggle: () => void;
  applicantResult?: CCAApplicantResult;
  isDetailLoading: boolean;
  isDetailError: boolean;
}

const ResultRow: React.FC<ResultRowProps> = ({
  summary,
  isExpanded,
  onToggle,
  applicantResult,
  isDetailLoading,
  isDetailError,
}) => {
  const scoreDisplay = getScoreDisplay(summary);
  return (
    <Box sx={{ borderBottom: '1px solid', borderColor: 'divider', '&:last-child': { borderBottom: 'none' } }}>
      <Button
        fullWidth
        onClick={onToggle}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          px: 2,
          py: 1.5,
          textAlign: 'left',
          textTransform: 'none',
          color: 'inherit',
          '&:hover': { bgcolor: 'action.hover' },
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <NameEmailAvatar
            name={`${summary.firstName} ${summary.lastName}`.trim() || summary.email || '—'}
            email={summary.email}
            avatar={undefined}
            avatarWidth='36px'
            avatarHeight='36px'
            nameFontSize='14px'
            emailFontSize='11px'
          />
        </Box>

        <Box sx={{ width: 100, flexShrink: 0, display: { xs: 'none', sm: 'block' } }}>
          <StatusBadge status={summary.status} />
        </Box>

        <Box sx={{ width: 80, flexShrink: 0 }}>
          {scoreDisplay != null ? (
            <Typography variant='body2' sx={{ fontWeight: 700, color: scoreDisplay.pct != null ? scoreColor(scoreDisplay.pct) : 'text.primary' }}>
              {scoreDisplay.text}
            </Typography>
          ) : summary.status === 'Completed' && summary.scoredAt == null ? (
            <Chip label='Pending' size='small' color='warning' variant='outlined' sx={{ fontSize: '0.65rem', height: 20 }} />
          ) : (
            <Typography variant='caption' sx={{ color: 'text.secondary' }}>
              —
            </Typography>
          )}
        </Box>

        <Box sx={{ width: 64, flexShrink: 0, display: { xs: 'none', sm: 'block' } }}>
          {summary.attemptNumber != null ? (
            <Chip label={`#${summary.attemptNumber}`} size='small' sx={{ fontSize: '0.65rem', height: 20 }} />
          ) : (
            <Typography variant='caption' sx={{ color: 'text.secondary' }}>
              —
            </Typography>
          )}
        </Box>

        <Box sx={{ width: 80, flexShrink: 0, display: { xs: 'none', lg: 'block' } }}>
          <Typography variant='caption' sx={{ fontWeight: 500, color: 'text.secondary' }}>
            {formatDuration(summary.activeTimeSeconds)}
          </Typography>
        </Box>

        <Box sx={{ width: 110, flexShrink: 0, display: { xs: 'none', md: 'block' } }}>
          <Typography variant='caption' sx={{ color: 'text.secondary' }}>
            {formatDate(summary.completedAt)}
          </Typography>
        </Box>

        <ChevronDown
          size={16}
          style={{
            transform: isExpanded ? 'rotate(180deg)' : 'none',
            transition: 'transform 0.2s',
            opacity: 0.5,
            flexShrink: 0,
          }}
        />
      </Button>

      {isExpanded && (
        applicantResult ? (
          <ResultDetail
            result={applicantResult}
            isLoading={isDetailLoading}
            isError={isDetailError}
          />
        ) : (
          <ResultDetail
            result={{
              assessment: {
                applicantId: summary.applicantId,
                jobOpeningId: '',
                status: summary.status,
                totalScore: null,
                maxScore: null,
                totalPct: null,
                completedAt: null,
                subdomainScores: {},
              },
              responses: [],
            }}
            isLoading={isDetailLoading}
            isError={isDetailError}
          />
        )
      )}
    </Box>
  );
};

// ─── Skeleton ─────────────────────────────────────────────────────────────────

const SkeletonRows: React.FC = () => (
  <>
    {Array.from({ length: 6 }).map((_, i) => (
      <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 2, px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Skeleton variant='circular' width={36} height={36} />
        <Box sx={{ flex: 1 }}>
          <Skeleton variant='text' width={120} height={20} />
          <Skeleton variant='text' width={180} height={16} />
        </Box>
        <Skeleton variant='rounded' width={80} height={24} sx={{ borderRadius: 3, display: { xs: 'none', sm: 'block' } }} />
        <Skeleton variant='text' width={60} height={20} />
      </Box>
    ))}
  </>
);

// ─── Main component ───────────────────────────────────────────────────────────

export interface CCAResultsSectionProps {
  jobOpeningId: string;
}

export const CCAResultsSection: React.FC<CCAResultsSectionProps> = ({
  jobOpeningId,
}) => {
  const {
    results,
    total,
    isListLoading,
    isListError,
    selectedApplicantId,
    selectApplicant,
    applicantResult,
    isDetailLoading,
    isDetailError,
  } = useCCAResults(jobOpeningId);

  if (isListError) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 8 }}>
        <Typography variant='body2' sx={{ fontWeight: 600, color: 'error.main' }}>
          Failed to load results.
        </Typography>
        <Typography variant='caption' sx={{ mt: 0.5, color: 'text.secondary' }}>
          Please refresh the page.
        </Typography>
      </Box>
    );
  }

  return (
    <Stack spacing={2}>
      {/* ── Header ── */}
      {!isListLoading && total > 0 && (
        <Typography variant='subtitle2' sx={{ fontWeight: 700, color: 'text.primary' }}>
          {total} result{total !== 1 ? 's' : ''}
        </Typography>
      )}

      {/* ── Results list ── */}
      <Paper variant='outlined' sx={{ borderRadius: 2.5, overflow: 'hidden', bgcolor: 'background.paper' }}>
        {/* Table header */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, px: 2, py: 1.25, borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'action.hover' }}>
          <Typography variant='caption' sx={{ flex: 1, fontWeight: 700, textTransform: 'uppercase', color: 'text.secondary', pl: 6 }}>
            Candidate
          </Typography>
          <Typography variant='caption' sx={{ width: 100, fontWeight: 700, textTransform: 'uppercase', color: 'text.secondary', display: { xs: 'none', sm: 'block' } }}>
            Status
          </Typography>
          <Typography variant='caption' sx={{ width: 80, fontWeight: 700, textTransform: 'uppercase', color: 'text.secondary' }}>
            Score
          </Typography>
          <Typography variant='caption' sx={{ width: 64, fontWeight: 700, textTransform: 'uppercase', color: 'text.secondary', display: { xs: 'none', sm: 'block' } }}>
            Attempt
          </Typography>
          <Typography variant='caption' sx={{ width: 80, fontWeight: 700, textTransform: 'uppercase', color: 'text.secondary', display: { xs: 'none', lg: 'block' } }}>
            Active Time
          </Typography>
          <Typography variant='caption' sx={{ width: 110, fontWeight: 700, textTransform: 'uppercase', color: 'text.secondary', display: { xs: 'none', md: 'block' } }}>
            Completed
          </Typography>
          <Box sx={{ width: 16 }} />
        </Box>

        {isListLoading ? (
          <SkeletonRows />
        ) : results.length === 0 ? (
          <Box sx={{ py: 6, px: 2, textAlign: 'center' }}>
            <Typography variant='body2' sx={{ fontWeight: 600, color: 'text.secondary' }}>
              No results yet
            </Typography>
            <Typography variant='caption' sx={{ mt: 0.5, color: 'text.disabled' }}>
              Switch to the Candidates tab to invite candidates to the assessment.
            </Typography>
          </Box>
        ) : (
          results.map((summary) => (
            <ResultRow
              key={summary.applicantId}
              summary={summary}
              isExpanded={selectedApplicantId === summary.applicantId}
              onToggle={() => selectApplicant(summary.applicantId)}
              applicantResult={
                selectedApplicantId === summary.applicantId
                  ? applicantResult
                  : undefined
              }
              isDetailLoading={
                selectedApplicantId === summary.applicantId && isDetailLoading
              }
              isDetailError={
                selectedApplicantId === summary.applicantId && isDetailError
              }
            />
          ))
        )}
      </Paper>
    </Stack>
  );
};

export default CCAResultsSection;
